import React, { useMemo, useRef, useState } from 'react';
import Modal, { ModalCloseButton } from './Modal';
import { downloadExport } from '../api';

// Lead-Export mit Ort-Auswahl: leer = alle Leads, sonst nur die gewählten Orte
export default function ExportModal({ leads, authHeader, onClose }) {
  const [selected, setSelected] = useState(() => new Set());
  const [filter, setFilter] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  // onClose geht über history.back() — darf nur einmal feuern (verzögertes Schließen nach
  // dem Download + manuelles Schließen würden sonst zwei History-Schritte zurückgehen)
  const closedRef = useRef(false);
  const close = () => { if (closedRef.current) return; closedRef.current = true; onClose(); };

  const orte = useMemo(() => {
    const counts = new Map();
    for (const l of leads) { const o = l.ort || '(ohne Ort)'; counts.set(o, (counts.get(o) || 0) + 1); }
    return [...counts.entries()].sort((a, b) => a[0].localeCompare(b[0], 'de'));
  }, [leads]);

  const q = filter.trim().toLowerCase();
  const visible = q ? orte.filter(([o]) => o.toLowerCase().includes(q)) : orte;
  const total = selected.size === 0 ? leads.length : orte.reduce((s, [o, n]) => s + (selected.has(o) ? n : 0), 0);

  const toggle = o => setSelected(prev => { const n = new Set(prev); n.has(o) ? n.delete(o) : n.add(o); return n; });

  const start = async () => {
    setBusy(true); setError('');
    try {
      const p = new URLSearchParams();
      // "(ohne Ort)" gibt es serverseitig nicht — Leads ohne Ort sind nur im Gesamtexport enthalten
      for (const o of selected) if (o !== '(ohne Ort)') p.append('ort', o);
      await downloadExport(`/api/leads/export/xlsx${p.toString() ? `?${p}` : ''}`, authHeader);
      // Modal erst schließen, wenn der Download sicher angelaufen ist — das Schließen geht
      // über history.back(), und das darf den Download-Request nicht abwürgen
      setTimeout(close, 800);
    } catch (err) {
      console.error('Export-Fehler:', err);
      setError('Export konnte nicht gestartet werden — bitte Verbindung prüfen und erneut versuchen.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal onClose={close} label="Leads exportieren" style={{ maxWidth: 460 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <h2>Leads exportieren</h2>
        <ModalCloseButton onClose={close} />
      </div>
      <p style={{ fontSize: 13, color: '#6b7280', marginBottom: 10 }}>
        Ohne Auswahl werden alle {leads.length} Leads exportiert. Mit Auswahl nur die gewählten Orte.
      </p>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 }}>
        <input
          value={filter}
          onChange={e => setFilter(e.target.value)}
          placeholder="Ort suchen…"
          aria-label="Orte filtern"
          style={{ flex: 1, padding: '8px 12px', border: '1px solid #e0ddff', borderRadius: 10, fontSize: 16, minWidth: 0 }}
        />
        {selected.size > 0 && (
          <button type="button" className="btn btn-secondary" style={{ minHeight: 36, padding: '6px 10px', fontSize: 12 }} onClick={() => setSelected(new Set())}>
            Auswahl leeren
          </button>
        )}
      </div>
      <div role="group" aria-label="Orte" style={{ maxHeight: 280, overflowY: 'auto', border: '1px solid #ede9fe', borderRadius: 10 }}>
        {visible.length === 0 && <div style={{ padding: 12, fontSize: 13, color: '#6b7280' }}>Kein Ort gefunden.</div>}
        {visible.map(([o, n]) => (
          <label key={o} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', borderBottom: '1px solid #f3f4f6', cursor: 'pointer', fontSize: 14 }}>
            <input type="checkbox" checked={selected.has(o)} onChange={() => toggle(o)} />
            <span style={{ flex: 1, color: '#1e1b4b' }}>{o}</span>
            <span style={{ fontSize: 12, color: '#6b7280' }}>{n}</span>
          </label>
        ))}
      </div>
      {error && <div className="login-error" role="alert" style={{ marginTop: 12 }}>{error}</div>}
      <div className="form-actions" style={{ marginTop: 16 }}>
        <button type="button" className="btn btn-secondary" onClick={close}>Abbrechen</button>
        <button
          type="button"
          className={`btn btn-gradient${busy ? ' btn-loading' : ''}`}
          onClick={start}
          disabled={busy || total === 0}
          aria-busy={busy}
        >
          {busy ? 'Startet…' : `${total} Leads exportieren`}
        </button>
      </div>
    </Modal>
  );
}
